import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Download, LogOut, Loader2, Lock, RefreshCw } from "lucide-react";
import { API } from "../lib/api";

const TOKEN_KEY = "ezp_admin_token";

function formatError(detail) {
    if (!detail) return "Something went wrong. Please try again.";
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail)) return detail.map((e) => e?.msg).filter(Boolean).join(" ");
    return String(detail?.msg ?? detail);
}

function LoginForm({ onLogin }) {
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError("");
        try {
            const { data } = await axios.post(`${API}/auth/login`, { email, password });
            onLogin(data?.token);
        } catch (err) {
            setError(formatError(err?.response?.data?.detail));
        } finally {
            setLoading(false);
        }
    };

    return (
        <section className="min-h-[70vh] flex items-center justify-center bg-[#F8FAFC] px-6 py-20">
            <form onSubmit={submit} className="w-full max-w-md bg-white border border-[#E2E8F0] p-8 md:p-10" data-testid="admin-login-form">
                <div className="w-12 h-12 bg-[#0A2540] text-[#C5A059] flex items-center justify-center"><Lock size={20} /></div>
                <h1 className="mt-6 text-3xl font-semibold text-[#0A2540]">Admin sign in</h1>
                <p className="mt-2 text-sm text-[#475569]">View and download your project leads.</p>
                <label className="mt-8 block text-xs font-semibold uppercase tracking-wide text-[#475569]">Email</label>
                <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="admin-login-email"
                    className="mt-1.5 w-full border border-[#E2E8F0] px-4 py-3 outline-none focus:border-[#C5A059]" />
                <label className="mt-4 block text-xs font-semibold uppercase tracking-wide text-[#475569]">Password</label>
                <input type="password" required value={password} onChange={(e) => setPassword(e.target.value)} data-testid="admin-login-password"
                    className="mt-1.5 w-full border border-[#E2E8F0] px-4 py-3 outline-none focus:border-[#C5A059]" />
                {error && <p className="mt-4 text-sm text-red-600" data-testid="admin-login-error">{error}</p>}
                <button type="submit" disabled={loading} data-testid="admin-login-submit"
                    className="btn-blue mt-6 w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 text-sm font-semibold disabled:opacity-60">
                    {loading && <Loader2 size={16} className="animate-spin" />} Sign in
                </button>
            </form>
        </section>
    );
}

function LeadsTable({ leads }) {
    if (leads.length === 0) {
        return <p className="p-8 text-center text-[#475569]" data-testid="admin-leads-empty">No leads yet.</p>;
    }
    return (
        <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="admin-leads-table">
                <thead className="bg-[#0A2540] text-white text-left">
                    <tr>
                        {["Date", "Name", "Contact", "Project", "City", "Source", "Details"].map((h) => (
                            <th key={h} className="px-4 py-3 font-semibold whitespace-nowrap">{h}</th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {leads.map((l) => (
                        <tr key={l.id} className="border-b border-[#E2E8F0] align-top" data-testid={`admin-lead-row-${l.id}`}>
                            <td className="px-4 py-3 whitespace-nowrap text-[#475569]">{l.created_at ? new Date(l.created_at).toLocaleDateString() : ""}</td>
                            <td className="px-4 py-3 font-medium text-[#0A2540] whitespace-nowrap">{l.name}</td>
                            <td className="px-4 py-3 text-[#0A2540]">
                                <a href={`tel:${l.phone}`} className="block hover:text-[#C5A059]">{l.phone}</a>
                                <a href={`mailto:${l.email}`} className="block text-[#475569] hover:text-[#C5A059]">{l.email}</a>
                            </td>
                            <td className="px-4 py-3 text-[#0A2540]">{l.project_type || "—"}</td>
                            <td className="px-4 py-3 text-[#0A2540]">{l.city || "—"}</td>
                            <td className="px-4 py-3"><span className="px-2 py-1 text-xs font-semibold uppercase tracking-wide bg-[#C5A059]/15 text-[#0A2540]">{l.source}</span></td>
                            <td className="px-4 py-3 text-[#475569] min-w-[260px]">{l.description || "—"}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

export default function Admin() {
    const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));
    const [leads, setLeads] = useState([]);
    const [loading, setLoading] = useState(false);

    const logout = useCallback(() => {
        localStorage.removeItem(TOKEN_KEY);
        setToken(null);
        setLeads([]);
    }, []);

    const load = useCallback(async () => {
        if (!token) return;
        setLoading(true);
        try {
            const { data } = await axios.get(`${API}/leads`, { headers: { Authorization: `Bearer ${token}` } });
            setLeads(Array.isArray(data) ? data : []);
        } catch (err) {
            if (err?.response?.status === 401) {
                toast.error("Session expired. Please sign in again.");
                logout();
            } else {
                toast.error("Could not load leads.");
            }
        } finally {
            setLoading(false);
        }
    }, [token, logout]);

    useEffect(() => {
        load();
    }, [load]);

    const handleLogin = (t) => {
        localStorage.setItem(TOKEN_KEY, t);
        setToken(t);
    };

    const download = async () => {
        try {
            const res = await axios.get(`${API}/leads/export`, { headers: { Authorization: `Bearer ${token}` }, responseType: "blob" });
            const url = URL.createObjectURL(res.data);
            const a = document.createElement("a");
            a.href = url;
            a.download = `ezpermit-leads-${new Date().toISOString().slice(0, 10)}.csv`;
            a.click();
            URL.revokeObjectURL(url);
        } catch {
            toast.error("Download failed.");
        }
    };

    if (!token) return <LoginForm onLogin={handleLogin} />;

    return (
        <section className="py-16 bg-[#F8FAFC] min-h-[70vh]" data-testid="admin-dashboard">
            <div className="max-w-7xl mx-auto px-6 lg:px-8">
                <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
                    <div>
                        <div className="uppercase tracking-[0.2em] text-xs font-semibold text-[#C5A059]">Admin</div>
                        <h1 className="mt-2 text-3xl lg:text-4xl font-semibold text-[#0A2540]">Leads</h1>
                        <p className="mt-2 text-[#475569]" data-testid="admin-leads-count">{leads.length} total</p>
                    </div>
                    <div className="flex flex-wrap gap-3">
                        <button onClick={load} className="btn-outline-blue inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium" data-testid="admin-refresh">
                            <RefreshCw size={15} className={loading ? "animate-spin" : ""} /> Refresh
                        </button>
                        <button onClick={download} className="btn-gold inline-flex items-center gap-2 px-4 py-2.5 text-sm font-semibold" data-testid="admin-download-csv">
                            <Download size={15} /> Download CSV
                        </button>
                        <button onClick={logout} className="inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium text-[#475569] hover:text-[#0A2540]" data-testid="admin-logout">
                            <LogOut size={15} /> Sign out
                        </button>
                    </div>
                </div>
                <div className="mt-8 bg-white border border-[#E2E8F0]">
                    <LeadsTable leads={leads} />
                </div>
            </div>
        </section>
    );
}
